/**
 * Bridges Firestore onSnapshot listeners into the TanStack Query cache.
 *
 * The query function owns the listener: the first snapshot resolves the
 * query, later snapshots are pushed straight into the cache. A module-level
 * registry (keyed by query hash) tracks every live listener so the cache
 * watcher can manage its lifecycle: when the last observer unmounts the
 * listener lingers for LINGER_MS (quick navigation reconnects for free),
 * then unsubscribes and marks the entry stale so the next mount restarts it.
 * Logout tears down user-scoped listeners immediately via
 * teardownUserSubscriptions; public listeners keep streaming.
 */
import {
  hashKey,
  type QueryClient,
  type QueryFunction,
} from "@tanstack/react-query";
import {
  DocumentReference,
  getDocsFromServer,
  onSnapshot,
  query as firestoreQuery,
  type DocumentSnapshot,
  type FirestoreError,
  type Query,
  type QuerySnapshot,
  type Unsubscribe,
} from "firebase/firestore";
import type { ZodType } from "zod";
import { attempt } from "@/utilities/attempt";
import { createRef } from "./paths";
import { processCollection, processDocument } from "./snapshot";
import type { FirestoreQueryKey } from "./queryKey";
import type { Options } from "./types";

export const LINGER_MS = 30_000;

type Subscription = {
  unsubscribe: Unsubscribe;
  lingerTimer?: ReturnType<typeof setTimeout>;
  userScoped: boolean;
};

const subscriptions = new Map<string, Subscription>();
let cacheUnsubscribe: (() => void) | null = null;

const clearLinger = (subscription: Subscription) => {
  if (subscription.lingerTimer) {
    clearTimeout(subscription.lingerTimer);
    subscription.lingerTimer = undefined;
  }
};

const dropSubscription = (hash: string) => {
  const subscription = subscriptions.get(hash);
  if (!subscription) return;
  clearLinger(subscription);
  subscriptions.delete(hash);
  subscription.unsubscribe();
};

const dropSubscriptionsWhere = (
  predicate: (subscription: Subscription) => boolean,
) => {
  for (const [hash, subscription] of Array.from(subscriptions.entries())) {
    if (predicate(subscription)) {
      dropSubscription(hash);
    }
  }
};

// When the linger expires: unsubscribe, cancel the (possibly never-settled)
// in-flight fetch so a remount refetches instead of deduping into a dead
// promise, and mark the entry stale so the next mount restarts the listener.
const armLinger = (
  client: QueryClient,
  hash: string,
  queryKey: readonly unknown[],
) => {
  const subscription = subscriptions.get(hash);
  if (!subscription) return;

  clearLinger(subscription);
  subscription.lingerTimer = setTimeout(() => {
    subscriptions.delete(hash);
    subscription.unsubscribe();
    void client.cancelQueries({ queryKey });
    client.invalidateQueries({ queryKey, refetchType: "none" });
  }, LINGER_MS);
};

// Listener lifecycle is driven by cache events: last observer gone arms the
// linger, a returning observer disarms it, cache eviction (external
// removeQueries/clear) must never leave an orphaned listener behind.
const watchTeardown = (client: QueryClient) => {
  if (cacheUnsubscribe) return;

  cacheUnsubscribe = client.getQueryCache().subscribe((event) => {
    const hash = event.query.queryHash;
    const subscription = subscriptions.get(hash);
    if (!subscription) return;

    switch (event.type) {
      case "observerRemoved":
        if (event.query.getObserversCount() === 0) {
          armLinger(client, hash, event.query.queryKey);
        }
        return;
      case "observerAdded":
        clearLinger(subscription);
        return;
      case "removed":
        dropSubscription(hash);
        return;
      default:
        return;
    }
  });
};

type SnapshotHandlers<TData> = {
  onData: (data: TData | null) => void;
  onError: (error: FirestoreError | Error) => void;
};

const subscribeToRef = <TData>(
  path: string,
  model: ZodType,
  options: Options<string | undefined>,
  { onData, onError }: SnapshotHandlers<TData>,
): Unsubscribe => {
  const ref = createRef(path);

  if (ref instanceof DocumentReference) {
    return onSnapshot(
      ref,
      { includeMetadataChanges: true },
      (snapshot: DocumentSnapshot) => {
        onData(processDocument(model, options, path)(snapshot) as TData);
      },
      onError,
    );
  }

  const refWithQuery: Query = options.query
    ? firestoreQuery(ref, ...options.query)
    : ref;

  return onSnapshot(
    refWithQuery,
    async (snapshot: QuerySnapshot) => {
      let resolvedSnapshot = snapshot;

      if (options.getFromServer && snapshot.metadata.fromCache) {
        const fetched = await attempt(() => getDocsFromServer(refWithQuery));
        if (!fetched.ok) {
          // listener stays alive and emits again once the SDK reaches the server
          console.error(
            "Failed to fetch collection from server:",
            path,
            fetched.error,
          );
          return;
        }
        resolvedSnapshot = fetched.data;
      }

      onData(
        processCollection(model, options, path)(resolvedSnapshot) as TData,
      );
    },
    onError,
  );
};

// A refetch can race a listener that is still alive (e.g. invalidation while
// lingering); serve the cache instead of double-subscribing. If the cache was
// emptied while the listener stayed alive (devtools reset/restore-loading/
// restore-error, removeQueries), the listener won't re-emit until the document
// changes — drop it so a fresh subscription repopulates the cache.
const reuseLiveListener = <TData>(
  client: QueryClient,
  hash: string,
  queryKey: FirestoreQueryKey,
): { data: TData } | null => {
  if (!subscriptions.has(hash)) return null;

  const cachedData = client.getQueryData<TData>(queryKey);
  if (cachedData !== undefined) {
    return { data: cachedData };
  }

  dropSubscription(hash);
  return null;
};

export const createFirestoreQueryFn =
  <TData>(
    model: ZodType,
    options: Options<string | undefined>,
  ): QueryFunction<TData | null, FirestoreQueryKey> =>
  ({ queryKey, client, meta }) =>
    new Promise<TData | null>((resolve, reject) => {
      const [, path] = queryKey;
      const hash = hashKey(queryKey);

      watchTeardown(client);

      const reusable = reuseLiveListener<TData>(client, hash, queryKey);
      if (reusable) {
        resolve(reusable.data);
        return;
      }

      // the first snapshot settles the query; later ones write straight
      // to the cache
      let settled = false;

      const onData = (data: TData | null) => {
        if (settled) {
          client.setQueryData<TData | null>(queryKey, data);
          return;
        }
        settled = true;
        resolve(data);
      };

      // listener errors are terminal: clean up, then either reject (no data
      // yet, TanStack retries) or invalidate (data on screen stays while the
      // refetch spins up a replacement listener)
      const listener: { unsubscribe?: Unsubscribe } = {};

      const onError = (error: FirestoreError | Error) => {
        console.error("ERROR", error);
        const subscription = subscriptions.get(hash);
        if (subscription) clearLinger(subscription);
        subscriptions.delete(hash);
        listener.unsubscribe?.();

        if (!settled) {
          settled = true;
          reject(error);
          return;
        }

        client.invalidateQueries({ queryKey });
      };

      // subscribe and register so the cache watcher can manage teardown
      listener.unsubscribe = subscribeToRef<TData>(path, model, options, {
        onData,
        onError,
      });

      subscriptions.set(hash, {
        unsubscribe: listener.unsubscribe,
        userScoped: meta?.userScoped === true,
      });
    });

// User-scoped listeners must die before the auth token is revoked on logout;
// public listeners are unaffected by auth changes and keep streaming.
export const teardownUserSubscriptions = () => {
  dropSubscriptionsWhere((subscription) => subscription.userScoped);
};

export const teardownAllSubscriptions = () => {
  dropSubscriptionsWhere(() => true);
  cacheUnsubscribe?.();
  cacheUnsubscribe = null;
};
